import { Chains, SessionKit, type Session } from "@wharfkit/session";
import { WalletPluginAnchor } from "@wharfkit/wallet-plugin-anchor";
import { WalletPluginCloudWallet } from "@wharfkit/wallet-plugin-cloudwallet";
import { WebRenderer } from "@wharfkit/web-renderer";
import { appName, chain, isMainnet } from "../config";

// WharfKit ships known-good definitions for WAX mainnet; testnet is
// supplied manually via config.ts (chain id / RPC url), since testnet
// endpoints rotate more often than WharfKit's release cycle. Verify
// src/config.ts's default testnet values against
// https://docs.wax.io/build/reference/rpc-endpoints/ before relying on them.
const activeChain = isMainnet
  ? Chains.WAX
  : { id: chain.id, url: chain.url };

export const sessionKit = new SessionKit({
  appName,
  chains: [activeChain],
  ui: new WebRenderer(),
  walletPlugins: [new WalletPluginCloudWallet(), new WalletPluginAnchor()],
});

/** Opens the wallet picker and returns the new session (or throws if cancelled). */
export async function login(): Promise<Session> {
  const response = await sessionKit.login();
  return response.session;
}

/** Silently restores a previous session (e.g. on page load) without prompting.
 * Unlike `login()`, `SessionKit.restore()` resolves directly to a `Session`
 * (or `undefined`) rather than a `{ session }`-shaped result — confirmed by
 * the real compiler error this produced on Netlify's build servers (this
 * sandbox couldn't type-check against WharfKit's actual installed types to
 * catch it earlier): `TS2339: Property 'session' does not exist on type
 * 'Session'` at the old `response?.session` line. */
export async function restore(): Promise<Session | undefined> {
  return sessionKit.restore();
}

export async function logout(session?: Session): Promise<void> {
  await sessionKit.logout(session);
}

export type { Session };
